import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, IconButton, TextField, Divider, Alert, Snackbar, Chip, CircularProgress, ButtonBase
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import default_theme from "assets/themes/themes";
import { blueTextFieldStyles } from "assets/themes/textFieldStyles";
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from "@mui/material";
import SolutionFormDialog from "components/CreateSolutionField/SolutionFormDialog";
import { createEmptySolutionForm } from "components/CreateSolutionField/solutionFormDefaults";
import DraftSolutions from "components/DraftSolutions/DraftSolutions";

import StepLayout from "../../../../components/StepsLayout/StepsLayout";
import DocumentItem from "components/Documents/StepZeroDocuments/stepZeroDocument";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { Paper } from "@mui/material";
import AddButton from "components/Common/AddButton";
import DangerButton from "components/Common/DangerButton";
import Grid from "@mui/material/Grid2";

// Redux Actions
import { fetchRddsRequest } from "../../../../redux/rdds/rddSlice";
import { fetchLocalCommunityTrainerRolesRequest } from "../../../../redux/localCommunityTrainerRoles/localCommunityTrainerRoleSlice";
import { fetchDomainsRequest } from "../../../../redux/domains/domainSlice";
import {
  fetchProjectDetailRequest,
  fetchProjectStakeholdersRequest,
  saveProjectStakeholdersRequest,
  fetchProjectInformationRequest,
  updateProjectInformationRequest,
} from "../../../../redux/projects/projectsSlice";
import { uploadFileRequest, getFileUrl } from "../../../../redux/files/filesSlice";
import {
  fetchTerritorialChallengesRequest,
  saveTerritorialChallengesRequest,
  deleteTerritorialChallengeRequest,
} from "../../../../redux/territorialChallenges/territorialChallengesSlice";

import { createDraftRequest, resetDraftState } from "../../../../redux/drafts/draftsSlice";

// Custom Components
import TooltipLabel from "components/Common/TooltipLabel";
import PhaseArrow from "components/Common/PhaseArrow";
import FigmaBlueDivider from "components/StepComponents/FigmaBlueDivider";
import styles from "../StepOne/StepOnePage.module.css";
import TerritorialChallengeDialog from "components/StepThreePage/TerritorialChallengeDialog";
import LocalCommunityTrainerSection from "components/StepThreePage/LocalCommunityTrainerSection";
import StepOneDialog from "components/StepOnePage/StepOneDialog";
import LocalActionPlanSection from "components/LocalActionPlan/LocalActionPlanSection";

import QuestionCardDownload from "components/StepThreePage/QuestionCardDownload";
import RuractiveCanvaDownload from "components/StepThreePage/RuractiveCanvaDownload";

import SolutionsCatalogDialog from "components/StepThreePage/SolutionsCatalogDialog";

import { 
  fetchSolutionProposalsRequest, 
  createSolutionProposalRequest, 
  updateSolutionProposalRequest, 
  deleteSolutionProposalRequest 
} from "../../../../redux/solutionProposals/solutionProposalsSlice";
import SolutionProposalDialog from "components/StepThreePage/SolutionProposalDialog";

import { openSolutionChat, sendMessageRequest } from "../../../../redux/agents/agentsSlice";
import SolutionChatPopup from "components/agents/SolutionChatPopup";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const StepThreePage = () => {
  const { projectId } = useParams();
  const dispatch = useDispatch();

  const rdds = useSelector((state) => state.rdds?.items || []);
  const domains = useSelector((state) => state.domains?.items || []);
  const lctRoles = useSelector((state) => state.localCommunityTrainerRoles?.items || []);

  const { currentProjectDetail, currentProjectInformation } = useSelector((state) => state.projects);
  const phase3Documents = currentProjectInformation?.documents || [];

  const [stakeholders, setStakeholders] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [formData, setFormData] = useState(null);

  const [openSolutionDialog, setOpenSolutionDialog] = useState(false);


  // State for the Future Scenario Text
  const [scenarioText, setScenarioText] = useState("");
  // State for the Strategic Vision Text
  const [strategicVision, setStrategicVision] = useState("");
  // Tracks if we have loaded the text from the database yet
  const [isTextInitialized, setIsTextInitialized] = useState(false);

  const [solutionForm, setSolutionForm] = useState(() => createEmptySolutionForm());
  const { createSuccess } = useSelector((state) => state.drafts);

  // Store cooldowns by challenge ID or index
  const [aiCooldowns, setAiCooldowns] = useState({});

  useEffect(() => {
    // Check if there are any active timers running
    const activeTimers = Object.values(aiCooldowns).some(time => time > 0);
    if (!activeTimers) return;

    const timer = setTimeout(() => {
      setAiCooldowns((prev) => {
        const nextCooldowns = { ...prev };
        let hasChanges = false;
        
        for (const key in nextCooldowns) {
          if (nextCooldowns[key] > 0) {
            nextCooldowns[key] -= 1;
            hasChanges = true;
          }
        }
        
        return hasChanges ? nextCooldowns : prev;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [aiCooldowns]);

  useEffect(() => {
    if (createSuccess) {
      setOpenSolutionDialog(false);
      setSolutionForm(createEmptySolutionForm());

      dispatch(resetDraftState());
    }
  }, [createSuccess, dispatch]);

  const handleSolutionChange = (event) => {
    const { name, value } = event.target;

    setSolutionForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveSolution = () => {
    dispatch(createDraftRequest({ 
      formData: solutionForm, 
      projectId: projectId 
    }));
  };
  // State for file upload errors
  const [fileError, setFileError] = useState("");

  // =========================
  // RESET LOCAL STATE ON PROJECT SWITCH
  // =========================
  useEffect(() => {
    setStakeholders([]);
    setScenarioText("");
    setStrategicVision("");
    setIsTextInitialized(false); 
    setLocalChallenges([]);
  }, [projectId]);

  // =========================
  // FETCH DATA ON MOUNT
  // =========================
  useEffect(() => {
    dispatch(fetchRddsRequest());
    dispatch(fetchDomainsRequest());
    dispatch(fetchLocalCommunityTrainerRolesRequest());

    if (projectId) {
      dispatch(fetchProjectDetailRequest({ projectId }));
      dispatch(fetchProjectStakeholdersRequest({ projectId }));
      dispatch(fetchProjectInformationRequest({ projectId }));
    }
  }, [dispatch, projectId]);

  // =========================
  // PREFILL STAKEHOLDERS & TEXT
  // =========================
  useEffect(() => {
    if (currentProjectDetail?.stakeholders) {
      setStakeholders(currentProjectDetail.stakeholders);
    }
  }, [currentProjectDetail]);
  // =========================
  // =========================
  // PREFILL TEXT BOXES
  // =========================
  useEffect(() => {
    // Only prefill if we have data AND we haven't initialized yet
    if (currentProjectInformation && !isTextInitialized) {
      setScenarioText(currentProjectInformation.future_scenario_text || "");
      setStrategicVision(currentProjectInformation.strategic_vision || "");
      setIsTextInitialized(true); // Mark as loaded so it never overwrites
    }
  }, [currentProjectInformation, isTextInitialized]);

  // =========================
  // DERIVED LCT LIST
  // =========================
  const localCommunityTrainers = stakeholders.filter(
    (s) => s.local_community_trainers
  );

  // =========================
  // ADD NEW LCT MEMBER
  // =========================
  const handleAddLocalCommunityTrainer = () => {
    setFormData({
      id: null,
      first_name: "",
      last_name: "",
      is_local_task_force_member: false,
      local_task_force_role_id: null,
      contact_details: "",
      brief_description: "",
      rdd_id: null,
      domain_id: null,
      age: "",
      gender: "",
      grue: "",
      local_community_trainers: "Yes",
      cross_cutting_priority: "",
      interest: 50,
      influence: 50,
    });

    setCurrentIndex(null);
    setOpenModal(true);
  };

  // =========================
  // SAVE / REMOVE LOGIC
  // =========================
  const handleSaveStakeholders = (updatedStakeholders) => {
    setStakeholders(updatedStakeholders);

    dispatch(
      saveProjectStakeholdersRequest({
        projectId,
        stakeholders: updatedStakeholders.map((s) => ({
          ...s,
          age: s.age === "" ? null : Number(s.age),
          gender: s.gender === "" ? null : s.gender,
          contact_details: s.contact_details || null,
          brief_description: s.brief_description || null,
          grue: s.grue || null,
          local_community_trainers: s.local_community_trainers || null,
          local_community_trainer_role_id: s.local_community_trainer_role_id || null,
          cross_cutting_priority: s.cross_cutting_priority || null,
          interest: s.interest ?? null,
          influence: s.influence ?? null,
        })),
      })
    );
  };

  // =========================
  // FILE MANAGEMENT (PHASE 3.2)
  // =========================
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setFileError("File exceeds the maximum size of 20 MB.");
      event.target.value = ""; // allows re-selecting the same file
      return;
    }

    setFileError("");

    dispatch(
      uploadFileRequest({
        file,
        onSuccess: (newFileId) => {
          const newFileRecord = {
            id: newFileId,
            name: file.name,
            url: getFileUrl(newFileId),
          };

          const updatedDocuments = [...phase3Documents, newFileRecord];

          dispatch(
            updateProjectInformationRequest({
              projectId,
              ...currentProjectInformation,
              documents: updatedDocuments,
            })
          );
        },
      })
    );
  };

  const handleDeleteFile = (fileIdToRemove) => {
    const updatedDocuments = phase3Documents.filter((doc) => doc.id !== fileIdToRemove);

    dispatch(
      updateProjectInformationRequest({
        projectId,
        ...currentProjectInformation,
        documents: updatedDocuments,
      })
    );
  };

  const handleSaveModal = () => {
    let updated;
    if (currentIndex !== null) {
      updated = stakeholders.map((s, i) => (i === currentIndex ? formData : s));
    } else {
      updated = [...stakeholders, formData];
    }
    handleSaveStakeholders(updated);
    setOpenModal(false);
  };
  // =========================
  // =========================
  // AUTO-SAVE HANDLERS
  // =========================
  const scenarioTimer = React.useRef(null);
  const visionTimer = React.useRef(null);

  const handleScenarioChange = (e) => {
    const newValue = e.target.value;
    setScenarioText(newValue); // Update the UI instantly

    // Clear the previous timer if the user is still typing
    if (scenarioTimer.current) clearTimeout(scenarioTimer.current);

    // Set a new timer to save after 1 second of inactivity
    scenarioTimer.current = setTimeout(() => {
      dispatch(
        updateProjectInformationRequest({
          projectId,
          ...currentProjectInformation,
          future_scenario_text: newValue,
        })
      );
    }, 1000);
  };

  const handleVisionChange = (e) => {
    const newValue = e.target.value;
    setStrategicVision(newValue);

    // Clear the previous timer if the user is still typing
    if (visionTimer.current) clearTimeout(visionTimer.current);

    // Set a new timer to save after 1 second of inactivity
    visionTimer.current = setTimeout(() => {
      dispatch(
        updateProjectInformationRequest({
          projectId,
          ...currentProjectInformation,
          // If empty, send null so the backend actually clears the database column
          strategic_vision: newValue,
        })
      );
    }, 1000);
  };
  // =========================
  // TERRITORIAL CHALLENGES STATE
  // =========================
  const territorialChallenges = useSelector((state) => state.territorialChallenges?.items || []);
  const [localChallenges, setLocalChallenges] = useState([]);
  const [challengeModalOpen, setChallengeModalOpen] = useState(false);
  const [challengeFormData, setChallengeFormData] = useState(null);
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(null);

  const solutionProposals = useSelector((state) => state.solutionProposals?.items || []);
  const [proposalModalOpen, setProposalModalOpen] = useState(false);
  const [proposalFormData, setProposalFormData] = useState(null);
  const [maxLimitWarning, setMaxLimitWarning] = useState(false);
  // Filter challenges to ONLY those selected for LW2
  const lw2Challenges = localChallenges.filter((c) => c.selected_for_lw2);
  
  // Fetch Proposals on Mount
  useEffect(() => {
    if (projectId) {
      dispatch(fetchSolutionProposalsRequest({ projectId }));
    }
  }, [dispatch, projectId]);

  const handleSaveProposal = (data) => {
    if (data.id) {
      dispatch(updateSolutionProposalRequest({ projectId, proposalId: data.id, payload: data, onSuccess: () => setProposalModalOpen(false) }));
    } else {
      dispatch(createSolutionProposalRequest({ projectId, payload: data, onSuccess: () => setProposalModalOpen(false) }));
    }
  };

  const handleDeleteProposal = (proposalId) => {
    dispatch(deleteSolutionProposalRequest({ projectId, proposalId }));
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [challengeToDelete, setChallengeToDelete] = useState(null);

  const confirmDeleteProposal = (proposalId) => {
    setProposalToDelete(proposalId);
    setDeleteDialogOpen(true);
  };

  const executeDeleteProposal = () => {
    if (proposalToDelete) {
      dispatch(deleteSolutionProposalRequest({ projectId, proposalId: proposalToDelete }));
    }
    setDeleteDialogOpen(false);
    setProposalToDelete(null);
  };

  // Fetch Challenges on Mount
  useEffect(() => {
    if (projectId) {
      dispatch(fetchTerritorialChallengesRequest({ projectId }));
    }
  }, [dispatch, projectId]);
  //  SYNC REDUX WITH LOCAL STATE
  useEffect(() => {
    if (territorialChallenges && territorialChallenges.length > 0) {
      const sortedChallenges = [...territorialChallenges].sort((a, b) => String(a.id).localeCompare(String(b.id)));
      setLocalChallenges(sortedChallenges);
    } else {
      setLocalChallenges([]);
    }
  }, [territorialChallenges]);
  // Handlers
  const handleAddChallenge = () => {
    setChallengeFormData({
      id: null,
      title: "",
      description: "",
      rdd_id: null,
      cross_cutting_priority: "",
    });
    setCurrentChallengeIndex(null);
    setChallengeModalOpen(true);
  };

  const handleSaveChallengeModal = () => {
    let updatedList;
    if (currentChallengeIndex !== null) {
      updatedList = localChallenges.map((c, i) => i === currentChallengeIndex ? challengeFormData : c);
    } else {
      updatedList = [...localChallenges, challengeFormData];
    }

    //  UPDATE LOCAL UI INSTANTLY (Fixes the refresh issue)
    setLocalChallenges(updatedList);

    dispatch(
      saveTerritorialChallengesRequest({
        projectId,
        territorial_challenges: updatedList
      }));


    setChallengeModalOpen(false);
  };

  const handleDeleteChallenge = (challengeId) => {
    // REMOVE FROM UI INSTANTLY
    setLocalChallenges(prev => prev.filter(c => c.id !== challengeId));


    dispatch(deleteTerritorialChallengeRequest({ projectId, challengeId }));
  };
  // =========================
  // LW2 SELECTION HANDLER
  // =========================
  const toggleLW2Selection = (challengeId, index) => {
    const challengeToToggle = localChallenges.find((c) => c.id === challengeId) || localChallenges[index];
    const willBeSelected = !challengeToToggle.selected_for_lw2;
    const cooldownKey = challengeId || index;

    if (willBeSelected) {
      const currentlySelectedCount = localChallenges.filter((c) => c.selected_for_lw2).length;
      
      if (currentlySelectedCount >= 8) {
        setMaxLimitWarning(true); 
        return; 
      }

      // Start a fresh 60-second cooldown specifically for this challenge
      setAiCooldowns(prev => ({ ...prev, [cooldownKey]: 60 }));
    }

    const updatedChallenges = localChallenges.map((challenge, i) => {
      const isMatch = challenge.id ? challenge.id === challengeId : i === index;
      if (isMatch) {
        return { ...challenge, selected_for_lw2: !challenge.selected_for_lw2 };
      }
      return challenge;
    });

    setLocalChallenges(updatedChallenges);

    dispatch(saveTerritorialChallengesRequest({
      projectId,
      territorial_challenges: updatedChallenges
    }));
  };

  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [activeChallengeForCatalog, setActiveChallengeForCatalog] = useState(null);

  const handleOpenCatalog = (challengeId) => {
    setActiveChallengeForCatalog(challengeId);
    setCatalogDialogOpen(true);
  };

  const handleAttachSolution = (solutionData) => {
    setLocalChallenges((prevChallenges) => {
      const updatedChallenges = prevChallenges.map((challenge) => {
        if (challenge.id === activeChallengeForCatalog) {
          const currentAttachments = challenge.attached_solutions || [];
          
          // Verify it's not already attached and we haven't hit the limit
          const isAlreadyAttached = currentAttachments.some(a => String(a.id) === String(solutionData.id));
          if (currentAttachments.length < 3 && !isAlreadyAttached) {
            return { ...challenge, attached_solutions: [...currentAttachments, solutionData] };
          }
        }
        return challenge;
      });

      // Dispatch the save action using the guaranteed new array
      dispatch(saveTerritorialChallengesRequest({ 
        projectId, 
        territorial_challenges: updatedChallenges 
      }));
      
      return updatedChallenges; // Updates UI instantly
    });
  };

  const handleRemoveAttachment = (challengeId, solutionIdToRemove) => {
    setLocalChallenges((prevChallenges) => {
      const updatedChallenges = prevChallenges.map((challenge) => {
        if (challenge.id === challengeId) {
          const filtered = (challenge.attached_solutions || []).filter(
            sol => String(sol.id) !== String(solutionIdToRemove)
          );
          return { ...challenge, attached_solutions: filtered };
        }
        return challenge;
      });

      // Dispatch the save action so the backend removes it from PostgreSQL
      dispatch(saveTerritorialChallengesRequest({ 
        projectId, 
        territorial_challenges: updatedChallenges 
      }));

      return updatedChallenges;
    });
  };
  // =========================
  // STALE DATA GUARD
  // =========================
  const isDataStale = currentProjectDetail && currentProjectDetail.id?.toString() !== projectId?.toString();

  const isLoading = !currentProjectDetail || isDataStale;

  if (isLoading) {
    return (
      <StepLayout step={3}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <Typography variant="h6" sx={{ color: "#7898A4" }}>
            Loading project data...
          </Typography>
        </Box>
      </StepLayout>
    );
  }

  return (
    <StepLayout step={3}>
      <Box sx={{ mt: 4, maxWidth: 900, mx: "auto" }}>


        {/* HERO */}
        <Typography variant="h5" className={styles.projectTitle}>
          Step 3: Co-create solutions for your territory
        </Typography>

        <Typography variant="body1" className={styles.introSubtitle}>
          You have built your RIE, identified and engaged your stakeholders, and
          established your Local Task Force.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          Now you will work with the RIE community to develop a shared vision,
          identify the challenges that matter most, explore potential solutions
          and turn the most promising ideas into actionable plans.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          This step is built around three Local Workshops. Each workshop builds
          on the results of the previous one.
        </Typography>

        <Box className={styles.checklist}>
          <Typography variant="h6" className={styles.phaseSectionTitle} sx={{ mb: 2 }}>
            What you will complete
          </Typography>

          {[
            {
              title: "Vision & Challenges",
              desc: "Develop a shared vision for your territory and identify the main challenges to address.",
            },
            {
              title: "Learning from Others",
              desc: "Explore existing solutions, adapt useful components and develop initial solution proposals.",
            },
            {
              title: "Fine-tuning Solutions",
              desc: "Develop detailed action plans for the most promising solutions, considering innovation, inclusion, cross-cutting priorities and implementation.",
            },
            {
              title: "Local Action Plan",
              desc: "Bring the results together into a structured plan for implementation.",
            },
          ].map((item) => (
            <Box className={styles.checkItem} key={item.title}>
              <CheckCircleOutlineIcon className={styles.checkIcon} />
              <Box>
                <Typography className={styles.checkText}>{item.title}</Typography>
                <Typography className={styles.checkDesc}>{item.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <FigmaBlueDivider />

        {/* ── INFO PANEL (learn): How the co-development process works ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              How the co-development process works
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 3 }}>
            The three Local Workshops form a continuous process.
          </Typography>

          {[
            {
              title: "1. VISION & CHALLENGES",
              subtitle: "Where do we want our territory to go?",
              text: "Together with stakeholders, develop a common vision of the territory and identify the current and future challenges that could prevent that vision from being achieved.",
            },
            {
              title: "2. LEARNING FROM OTHERS",
              subtitle: "What could help us address these challenges?",
              text: "Explore existing solutions and good practices. Identify useful components and discuss how they could be adapted to your local context.",
            },
            {
              title: "3. FINE-TUNING SOLUTIONS",
              subtitle: "How can we make the solution work here?",
              text: "Develop the most promising solutions into concrete action plans, considering resources, stakeholders, innovation, inclusion, cross-cutting priorities, implementation and long-term impact.",
            },
            {
              title: "LOCAL ACTION PLAN",
              subtitle: "How will we move from ideas to implementation?",
              text: "Bring together the vision, challenges, selected solutions and implementation plans into your Local Action Plan.",
            },
          ].map((step, index, arr) => (
            <Box key={step.title} sx={{ display: "flex", gap: 2, mb: index < arr.length - 1 ? 0 : 0 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #35B3AB, #23576B)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    flexShrink: 0,
                    boxShadow: "0 2px 8px rgba(53, 179, 171, 0.3)",
                  }}
                >
                  {index < 3 ? `0${index + 1}` : "✓"}
                </Box>
                {index < arr.length - 1 && (
                  <Box
                    sx={{
                      width: 2,
                      flex: 1,
                      minHeight: 30,
                      background: "linear-gradient(to bottom, #35B3AB, rgba(53, 179, 171, 0.2))",
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  p: 2.5,
                  mb: index < arr.length - 1 ? 3 : 0,
                  border: "1px solid rgba(35, 87, 107, 0.1)",
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, rgba(35, 87, 107, 0.03), rgba(53, 179, 171, 0.02))",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B", mb: 0.5 }}>
                  {step.title}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
                  {step.subtitle}
                </Typography>
                <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7 }}>
                  {step.text}
                </Typography>
              </Box>
            </Box>
          ))}

          <Typography variant="body1" sx={{ color: "#7898A4", mt: 2 }}>
            This progression reflects the role of the three workshops in the RURACTIVE methodology.
          </Typography>
        </Box>

        {/* ── INFO PANEL (learn): Make participation work for everyone ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              Make participation work for everyone
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            The quality of the co-development process depends on how people participate.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Before and during every workshop, consider:
          </Typography>

          {[
            { label: "Accessibility", text: "Make sure the location, materials and activities are accessible to participants with different needs." },
            { label: "Timing", text: "Choose times that work for people with different work and caregiving responsibilities." },
            { label: "Communication", text: "Use language and materials that are easy for everyone to understand." },
            { label: "Representation", text: "Make sure different genders, generations, backgrounds, abilities and perspectives can contribute." },
            { label: "Facilitation", text: "Create a space where people can speak openly, listen to one another and contribute to shared decisions." },
          ].map((item) => (
            <Box className={styles.checkItem} key={item.label}>
              <CheckCircleOutlineIcon className={styles.checkIcon} />
              <Box>
                <Typography className={styles.checkText} sx={{ fontWeight: 600, color: "#23576B" }}>{item.label}</Typography>
                <Typography className={styles.checkDesc}>{item.text}</Typography>
              </Box>
            </Box>
          ))}

          <Typography variant="body1" sx={{ color: "#7898A4", mt: 2 }}>
            The goal is not simply to collect individual opinions. The workshops should help the RIE community develop shared understanding and co-create common outputs.
          </Typography>

        </Box>

        {/* ── PHASE 3.2 — LOCAL WORKSHOPS ── */}
        <FigmaBlueDivider sx={{ mt: 6 }} />

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 4, color: "#23576B" }}>
          <PhaseArrow />
          Phase 3.2: Co-development activities
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#35B3AB", mb: 1 }}>
            Local Workshops
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
            The Local Workshops are the core co-development activities of the RIE.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
            Across three workshops, the RIE community will move from understanding the future of the territory to developing concrete solutions.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 4 }}>
            The Local Task Force supports the process throughout, helping refine the inputs and outputs of each workshop.
          </Typography>

          {/* ── Workshop journey ── */}
          <Typography variant="h6" className={styles.phaseSectionTitle} sx={{ mb: 3 }}>
            Workshop journey
          </Typography>

          {[
            {
              num: "01",
              title: "Workshop 1",
              eyebrow: "VISION & CHALLENGES",
              flow: "Shared vision → Territorial challenges",
            },
            {
              num: "02",
              title: "Workshop 2",
              eyebrow: "LEARNING FROM OTHERS",
              flow: "Challenges → Existing solutions → Initial solution proposals",
            },
            {
              num: "03",
              title: "Workshop 3",
              eyebrow: "FINE-TUNING SOLUTIONS",
              flow: "Solution proposals → Detailed action plans",
            },
          ].map((ws, index, arr) => (
            <Box key={ws.num} sx={{ display: "flex", gap: 2, mb: index < arr.length - 1 ? 0 : 0 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #35B3AB, #23576B)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    flexShrink: 0,
                    boxShadow: "0 2px 8px rgba(53, 179, 171, 0.3)",
                  }}
                >
                  {ws.num}
                </Box>
                {index < arr.length - 1 && (
                  <Box
                    sx={{
                      width: 2,
                      flex: 1,
                      minHeight: 40,
                      background: "linear-gradient(to bottom, #35B3AB, rgba(53, 179, 171, 0.2))",
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  p: 2.5,
                  mb: index < arr.length - 1 ? 3 : 0,
                  border: "1px solid rgba(35, 87, 107, 0.1)",
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, rgba(35, 87, 107, 0.03), rgba(53, 179, 171, 0.02))",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B", mb: 0.5 }}>
                  {ws.title}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#35B3AB", mb: 1, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: "0.8rem" }}>
                  {ws.eyebrow}
                </Typography>
                <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7 }}>
                  {ws.flow}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* =========================
           SECTION 1: LOCAL COMMUNITY TRAINERS
        ========================= */}
{/*
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 4, color: "#23576B" }}>
          <PhaseArrow />
          Phase 3.1: Enroling Local Community Trainers and ensuring active participation.
        </Typography>

        <LocalCommunityTrainerSection
          stakeholders={stakeholders}
          setStakeholders={setStakeholders}
          localCommunityTrainers={localCommunityTrainers}
          lctRoles={lctRoles}
          projectId={projectId}
          dispatch={dispatch}
          onAddNewMember={handleAddLocalCommunityTrainer}
          setFormData={setFormData}
          setCurrentIndex={setCurrentIndex}
          setOpenModal={setOpenModal}
          onSaveStakeholders={handleSaveStakeholders}
        />
*/}
        {/* =========================
           STAKEHOLDER MODAL
        ========================= */}
{/*
        <StepOneDialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSave={handleSaveModal}
          formData={formData}
          setFormData={setFormData}
          currentIndex={currentIndex}
          rdds={rdds}
          domains={domains}
        />

        <FigmaBlueDivider sx={{ mt: 6 }} />
*/}
        {/* =========================
           SECTION 2: EXPANDABLE LIST (PHASE 3.2)
        ========================= */}
        <Box sx={{ mt: 6 }}>
          

          {/* MAIN TITLE: LW1 */}
          <Box sx={{ mt: 8 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#7898A4", mb: 3 }}
            >
              LOCAL WORKSHOP 1 — VISION & CHALLENGES
            </Typography>

            <Typography variant="body1" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
              Build a shared vision and identify challenges
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
              The first Local Workshop brings the RIE community together to imagine the future of the territory and identify the key challenges that need to be addressed to move towards that future.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 3 }}>
              The challenges should emerge from the collective discussion and shared vision, rather than being treated as individual stakeholder problems.
            </Typography>

            <Box sx={{ p: 2, mb: 3, borderRadius: "8px", backgroundColor: "#f5f7fb", borderLeft: `4px solid #35B3AB` }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#23576B" }}>
                Expected outcome
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", fontStyle: "italic", color: "text.secondary", lineHeight: 1.6 }}>
                A shared territorial vision + a prioritised set of territorial challenges
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 1 }}>
              The workshop is designed as a full-day activity of approximately 5 hours, including breaks.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
              <strong>Participants:</strong> RIE Coordinator, Local Task Force and other relevant stakeholders.
            </Typography>

          </Box>

          {/* ── INFO PANEL (learn): Before the workshop ── */}
          <Box className={styles.phaseInfoPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Typography variant="h6" className={styles.phaseSectionTitle}>
                Before the workshop
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
              Prepare a future scenario together with the LTF.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
              The scenario should help participants think about how the territory could evolve in relation to the selected RDDs and cross-cutting priorities.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7 }}>
              During the workshop, use the scenario as a starting point for discussion rather than asking participants to simply react to it.
            </Typography>
          </Box>

          {/* ── INFO PANEL (learn): During the workshop ── */}
          <Box className={styles.phaseInfoPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Typography variant="h6" className={styles.phaseSectionTitle}>
                During the workshop
              </Typography>
            </Box>

            {[
              {
                step: "01",
                title: "Explore the future",
                text: "Invite participants to imagine the territory in the future.",
                question: "How do you imagine your territory to be? What do you hope or dream for your territory?",
                bullets: [
                  "Quality of life and services",
                  "Work, education, health and well-being",
                  "Social change and care for others",
                  "Environment, biodiversity and natural resources",
                  "Technology and infrastructure",
                ],
              },
              {
                step: "02",
                title: "Build the shared vision",
                text: "Bring the different perspectives together into a common territorial vision.",
                question: null,
                bullets: [],
              },
              {
                step: "03",
                title: "Identify challenges",
                text: "Ask participants to identify the current and future difficulties, gaps or changes that could prevent the territory from reaching the shared vision.",
                question: null,
                bullets: [],
                note: "A challenge should describe a significant issue or difficulty in the territory, connected to one or more RDDs and potentially involving cross-cutting priorities and gender considerations.",
              },
              {
                step: "04",
                title: "Refine together",
                text: "Review the challenges with the group and consolidate overlapping ideas.",
                question: null,
                bullets: [],
              },
            ].map((item, index, arr) => (
              <Box key={item.step} sx={{ display: "flex", gap: 2, mb: index < arr.length - 1 ? 0 : 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #35B3AB, #23576B)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(53, 179, 171, 0.3)",
                    }}
                  >
                    {item.step}
                  </Box>
                  {index < arr.length - 1 && (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        minHeight: 30,
                        background: "linear-gradient(to bottom, #35B3AB, rgba(53, 179, 171, 0.2))",
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    p: 2.5,
                    mb: index < arr.length - 1 ? 3 : 0,
                    border: "1px solid rgba(35, 87, 107, 0.1)",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, rgba(35, 87, 107, 0.03), rgba(53, 179, 171, 0.02))",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7, mb: item.question || item.bullets.length > 0 ? 1 : 0 }}>
                    {item.text}
                  </Typography>
                  {item.question && (
                    <Typography variant="body2" sx={{ color: "#23576B", fontWeight: 600, fontStyle: "italic", mb: item.bullets.length > 0 ? 1.5 : 0, pl: 2, borderLeft: "3px solid #35B3AB" }}>
                      "{item.question}"
                    </Typography>
                  )}
                  {item.bullets.length > 0 && (
                    <Typography variant="body2" sx={{ color: "#7898A4", mb: 0.5 }}>
                      Explore perspectives related to:
                    </Typography>
                  )}
                  {item.bullets.length > 0 && (
                    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: item.note ? 1.5 : 0 }}>
                      {item.bullets.map((b) => (
                        <li key={b}>
                          <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7 }}>{b}</Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                  {item.note && (
                    <Box sx={{ p: 1.5, mt: 1, borderRadius: "8px", backgroundColor: "rgba(53, 179, 171, 0.06)", border: "1px solid rgba(53, 179, 171, 0.15)" }}>
                      <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.6, fontSize: "0.85rem" }}>
                        {item.note}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

            {/* =========================
               SUBSECTION 1: FUTURE SCENARIO
            ========================= */}
            <Box className={styles.phaseDataPanel}>
              <Box className={styles.phaseSectionLabel}>
                <Box className={styles.phaseSectionDot} />
                <Typography variant="h6" className={styles.phaseSectionTitle}>
                  Future Scenario
                </Typography>
              </Box>

              <TooltipLabel
                label="About future scenarios"
                tooltip="You should develop future scenarios extending to the year 2050 for each selected RDD that highlight present and future challenges of your territory. These scenarios are grounded in your
                        specific geographic location and incorporate relevant projections for that area, particularly in
                        relation to the crosscutting priorities. Be sure to include local climate data, emerging trends,
                        demographic shifts, environmental challenges, economic developments, and any other context-
                        specific issues that are known to you."
              />

              {/* FILE UPLOAD SECTION */}
              <Paper
                sx={{
                  p: 2.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderRadius: "10px",
                  mb: 3,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: "#23576B",
                    }}
                  >
                    Upload Your Future Scenario Document
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      color: fileError ? "error.main" : "text.secondary",
                      fontWeight: fileError ? 600 : 400,
                    }}
                  >
                    {fileError ||
                      "Supported formats: PDF, DOC, DOCX • Maximum file size: 20 MB"}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    backgroundColor: default_theme.palette.other.addButtonBlue,
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: default_theme.palette.other.figmaBlue,
                      opacity: 0.9,
                    },
                  }}
                >
                  Upload
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileUpload}
                  />
                </Button>
              </Paper>

              {/* Render Uploaded Files mapped from Redux State */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {phase3Documents.map((doc) => (
                  <Box key={doc.id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <DocumentItem
                        title={doc.name}
                        file={doc.url}
                      />
                    </Box>
                    <IconButton
                      onClick={() => setFileToDelete(doc.id)}
                      sx={{ color: "#d32f2f" }}
                      aria-label="delete document"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              {/* TEXT INPUT SECTION for Future Scenario */}
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={{ color: "#888" }}>OR</Typography>
                </Divider>

                <Typography variant="subtitle2" sx={{ mb: 1, color: "#23576B", fontWeight: 600 }}>
                  Insert your co-created local future scenario text here:
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  placeholder="Write or paste your text here (Max 3000 characters)"
                  value={scenarioText}
                  onChange={handleScenarioChange}
                  inputProps={{ maxLength: 3000 }}
                  InputProps={{ sx: { backgroundColor: "#fff" } }}
                  helperText={`${scenarioText.length} / 3000 characters`}
                  sx={blueTextFieldStyles}
                />
              </Box>
            </Box>

            {/* =========================
               NICE SEPARATOR LINE
            ========================= */}
            <Divider
              sx={{
                my: 6,
                borderStyle: "dashed",
                borderColor: "#A9C1CD",
                borderWidth: "1px",
                width: "80%",
                mx: "auto"
              }}
            />

            {/* =========================
               SUBSECTION 2: STRATEGIC VISION
            ========================= */}
            <Box className={styles.phaseDataPanel}>
              <Box className={styles.phaseSectionLabel}>
                <Box className={styles.phaseSectionDot} />
                <Typography variant="h6" className={styles.phaseSectionTitle}>
                  Strategic Vision
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mb: 3, color: "#555", lineHeight: 1.6 }}>
                <strong>Questions</strong> to help reflecting on the vision.
                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                  <li><strong>MAIN QUESTION: How do you imagine your territory to be/what do you hope/dream for your territory in 2050?</strong></li>
                  <li>What do you imagine in relation to the quality of life and services, including work, education, health, and wellbeing?</li>
                  <li>What kind of social change and care for others do you wish for?</li>
                  <li>What do you imagine in relation to caring for the environment, biodiversity and natural resources?</li>
                  <li>How do you imagine the development of technology and infrastructure?</li>
                </ul>
              </Typography>

              {/* TEXT INPUT SECTION for Strategic Vision */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#23576B", fontWeight: 600 }}>
                  Write your 15-month territorial vision here:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  variant="outlined"
                  placeholder="Write or paste your text here (Max 3000 characters)"
                  value={strategicVision}
                  onChange={handleVisionChange}
                  inputProps={{ maxLength: 3000 }}
                  InputProps={{ sx: { backgroundColor: "#fff" } }}
                  helperText={`${strategicVision.length} / 3000 characters`}
                  sx={blueTextFieldStyles}
                />
              </Box>
            </Box>

          <Divider
            sx={{
              my: 6,
              borderStyle: "dashed",
              borderColor: "#A9C1CD",
              borderWidth: "1px",
              width: "80%",
              mx: "auto"
            }}
          />
          {/* =========================
               SUBSECTION 3: TERRITORIAL CHALLENGES
            ========================= */}
          <Box className={styles.phaseDataPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Typography variant="h6" className={styles.phaseSectionTitle}>
                Identify Territorial Challenges
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 3, color: "#555", lineHeight: 1.6 }}>
              Based on the scenario and your daily life/work, add the specific, difficult issues or gaps that currently exist or may arise in the future.
            </Typography>
            <Box
              sx={{
                maxHeight: "550px", 
                overflowY: "auto",
                overflowX: "hidden",
                p: 2, 
                backgroundColor: "#F4F7F9",
                borderRadius: "8px", 
                border: "1px solid #E4E7EC", 
                boxShadow: "inset 0 4px 6px rgba(0,0,0,0.04)", 
                pr: 1
              }}
            >
              {/* CHALLENGE CARDS GRID */}
              <Grid container spacing={2}>
                {localChallenges.map((challenge, index) => {
                  const rdd = rdds.find((item) => item.id === challenge.rdd_id);

                  return (
                    <Grid size={{ xs: 12 }} key={challenge.id || index}>
                      <Paper sx={{ borderRadius: 4, p: 2.5, display: "flex", flexDirection: "column", height: "100%", userSelect: "none", transition: "all 0.2s ease-in-out", border: "1px solid #23576B", backgroundColor: "#FAFCFD", boxShadow: "0 6px 20px rgba(35, 87, 107, 0.12)", "&:hover": { boxShadow: "0 8px 24px rgba(35, 87, 107, 0.15)" } }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#23576B", mb: 1, wordBreak: "break-word", overflowWrap: "break-word" }}>
                          {challenge.title}
                        </Typography>

                        <Typography variant="body2" sx={{ color: "#667085", mb: 2, flex: 1, whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "break-word" }}>
                          {challenge.description}
                        </Typography>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                          {rdd && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 1.2, py: 0.4, borderRadius: 2, backgroundColor: "#F5F7FA", border: "1px solid #D0D5DD" }}>
                              {rdd.image_url && <Box component="img" src={rdd.image_url} alt={rdd.name} sx={{ width: 16, height: 16 }} />}
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#344054" }}>{rdd.name}</Typography>
                            </Box>
                          )}
                          {challenge.cross_cutting_priority && (
                            <Box sx={{ px: 1.2, py: 0.4, borderRadius: 2, backgroundColor: "#F5F7FA", border: "1px solid #D0D5DD" }}>
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#344054" }}>CCP: {challenge.cross_cutting_priority}</Typography>
                            </Box>
                          )}
                        </Box>

                        {/* BUTTON ACTIONS GROUP */}
                        <Box sx={{ display: "flex", gap: 1, mt: "auto", flexWrap: "wrap" }}>
                          <Button
                            onClick={() => {
                              setChallengeFormData({ ...challenge });
                              setCurrentChallengeIndex(index);
                              setChallengeModalOpen(true);
                            }}
                            sx={{ flex: 1, minWidth: "110px", backgroundColor: "#23576B", color: "#fff", borderRadius: 2, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#1B4454" } }}
                          >
                            Edit Challenge
                          </Button>

                          {/* "ADD TO LW2" BUTTON NEXT TO DELETE */}
                          <Button
                            variant={challenge.selected_for_lw2 ? "outlined" : "contained"}
                            onClick={() => toggleLW2Selection(challenge.id, index)}
                            sx={{
                              flex: 1.2,
                              minWidth: "120px",
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              ...(challenge.selected_for_lw2
                                ? { borderColor: default_theme.palette.other.chatGreen, color: default_theme.palette.other.chatGreen, "&:hover": { backgroundColor: "#E8F5E9", borderColor: "#1B5E20" } }
                                : { backgroundColor: default_theme.palette.other.chatGreen, color: "#fff", "&:hover": { backgroundColor: default_theme.palette.other.chatGreenHover } }),
                            }}
                          >
                            {challenge.selected_for_lw2 ? "Added to LW2" : "Add to LW2"}
                          </Button>

                          <DangerButton onClick={() => setChallengeToDelete(challenge.id)} sx={{ minWidth: "80px" }}>
                            Delete
                          </DangerButton>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            {/* ADD BUTTON */}
            <Box sx={{ mt: 3 }}>
              <AddButton onClick={handleAddChallenge}>+ Add a New Challenge</AddButton>
            </Box>
          </Box>

          {/* DIALOG */}
          <TerritorialChallengeDialog
            open={challengeModalOpen}
            onClose={() => setChallengeModalOpen(false)}
            onSave={handleSaveChallengeModal}
            formData={challengeFormData}
            setFormData={setChallengeFormData}
            rdds={rdds}
          />

          <Divider sx={{ mt: 4, mb: 4 }} />

          <Box sx={{ mt: 8 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#7898A4", mb: 3 }}
            >
              LOCAL WORKSHOP 2 — LEARNING FROM OTHERS
            </Typography>

            <Typography variant="body1" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
              Explore solutions and adapt them to your territory
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
              The second Local Workshop starts from the challenges identified in Workshop 1.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
              Together with stakeholders, explore existing solutions and good practices from the RURACTIVE Solutions Catalogue. Use them as inspiration to identify approaches, components or ideas that could help address your local challenges.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 3 }}>
              The objective is not to copy an existing solution. It is to understand what works elsewhere and explore how relevant elements could be adapted to your territory.
            </Typography>

            <Box sx={{ p: 2, mb: 3, borderRadius: "8px", backgroundColor: "#f5f7fb", borderLeft: `4px solid #35B3AB` }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#23576B" }}>
                Expected outcome
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", fontStyle: "italic", color: "text.secondary", lineHeight: 1.6 }}>
                A shortlist of initial solution proposals
              </Typography>
            </Box>
          </Box>

          {/* ── INFO PANEL (learn): From challenge to solution ── */}
          <Box className={styles.phaseInfoPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Typography variant="h6" className={styles.phaseSectionTitle}>
                From challenge to solution
              </Typography>
            </Box>

            {[
              {
                step: "01",
                title: "Select challenges",
                text: "Choose the challenges from Workshop 1 that should be explored further.",
              },
              {
                step: "02",
                title: "Explore existing solutions",
                text: "Use the Solutions Catalogue, Wizard and solution factsheets to find relevant examples.",
              },
              {
                step: "03",
                title: "Understand the solution",
                text: "Look beyond the solution title.",
                bullets: [
                  "What problem does it address?",
                  "How does it work?",
                  "What activities does it involve?",
                  "What resources does it require?",
                  "Who is involved?",
                  "What could be transferable to your territory?",
                ],
              },
              {
                step: "04",
                title: "Adapt",
                text: "Discuss which components could work locally and what would need to change.",
              },
              {
                step: "05",
                title: "Develop proposals",
                text: "Combine useful ideas and local knowledge into initial solution proposals.",
              },
            ].map((item, index, arr) => (
              <Box key={item.step} sx={{ display: "flex", gap: 2, mb: index < arr.length - 1 ? 0 : 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #35B3AB, #23576B)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(53, 179, 171, 0.3)",
                    }}
                  >
                    {item.step}
                  </Box>
                  {index < arr.length - 1 && (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        minHeight: 30,
                        background: "linear-gradient(to bottom, #35B3AB, rgba(53, 179, 171, 0.2))",
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    p: 2.5,
                    mb: index < arr.length - 1 ? 3 : 0,
                    border: "1px solid rgba(35, 87, 107, 0.1)",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, rgba(35, 87, 107, 0.03), rgba(53, 179, 171, 0.02))",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7, mb: item.bullets ? 1 : 0 }}>
                    {item.text}
                  </Typography>
                  {item.bullets && (
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {item.bullets.map((b) => (
                        <li key={b}>
                          <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7 }}>{b}</Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}

            <Typography variant="body1" sx={{ color: "#7898A4", mt: 2 }}>
              Existing solutions are a source of inspiration. The aim is to develop solutions that respond to your territory's specific context and challenges.
            </Typography>
          </Box>

          <Box className={styles.phaseDataPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Typography variant="h6" className={styles.phaseSectionTitle}>
                Challenges Selected for Workshop
              </Typography>
              <Box sx={{ backgroundColor: "#35B3AB", color: "white", px: 1, py: 0.2, borderRadius: 2, fontSize: "0.8rem", ml: 1 }}>
                {localChallenges.filter((challenge) => challenge.selected_for_lw2).length}
              </Box>
            </Box>

              {localChallenges.filter((challenge) => challenge.selected_for_lw2).length === 0 ? (
                <Typography variant="body2" sx={{ p: 2, borderRadius: 2, backgroundColor: "#F8FAFC", border: "1px solid #E4E7EC", borderLeft: "4px solid #23576B", color: "#475467", fontWeight: 500 }}>
                  No challenges selected yet. Click "Add to LW2" on a challenge card in the "Identify Territorial Challenges" section above.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: "580px", overflowY: "auto", overflowX: "hidden", pr: 1, backgroundColor: "#F4F7F9", borderRadius: "8px", border: "1px solid #E4E7EC",boxShadow: "inset 0 4px 6px rgba(0,0,0,0.04)",p:2}}>
                  <Grid container spacing={2}>
                    {localChallenges.filter((challenge) => challenge.selected_for_lw2).map((challenge, index) => {
                      const rdd = rdds.find((item) => item.id === challenge.rdd_id);
                      const globalIndex = localChallenges.findIndex((item) => item.id === challenge.id);

                      return (
                        <Grid size={{ xs: 12 }} key={`selected-${challenge.id || index}`}>
                          <Paper sx={{ borderRadius: 4, p: 2.5, display: "flex", flexDirection: "column", height: "100%", userSelect: "none", transition: "all 0.2s ease-in-out", border: "1px solid #23576B", backgroundColor: "#FAFCFD", boxShadow: "0 6px 20px rgba(35, 87, 107, 0.12)", "&:hover": { boxShadow: "0 8px 24px rgba(35, 87, 107, 0.15)" } }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#23576B", wordBreak: "break-word", mb: 1 }}>
                              {challenge.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#667085", mb: 2, flex: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {challenge.description}
                            </Typography>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                              {rdd && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 1.2, py: 0.4, borderRadius: 2, backgroundColor: "#F5F7FA", border: "1px solid #D0D5DD" }}>
                                  {rdd.image_url && <Box component="img" src={rdd.image_url} alt={rdd.name} sx={{ width: 16, height: 16 }} />}
                                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#344054" }}>{rdd.name}</Typography>
                                </Box>
                              )}
                              {challenge.cross_cutting_priority && (
                                <Box sx={{ px: 1.2, py: 0.4, borderRadius: 2, backgroundColor: "#F5F7FA", border: "1px solid #D0D5DD" }}>
                                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#344054" }}>CCP: {challenge.cross_cutting_priority}</Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Render Attached Solutions */}
                            {(challenge.attached_solutions && challenge.attached_solutions.length > 0) && (
                              <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {challenge.attached_solutions.map(sol => (
                                  <Chip 
                                    key={sol.id}
                                    label={sol.title}
                                    onDelete={() => handleRemoveAttachment(challenge.id, sol.id)}
                                    onClick={() => window.open(`/solutions/rural-development/solutions-catalog/${sol.id}`, '_blank')}
                                    icon={<OpenInNewIcon fontSize="small" />}
                                    sx={{ 
                                      backgroundColor: "#E8F5E9", 
                                      color: "#1B5E20", 
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      "&:hover": { backgroundColor: "#C8E6C9" }
                                    }}
                                  />
                                ))}
                              </Box>
                            )}

                            {/* Buttons */}
                            <Box sx={{ mt: "auto", pt: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                              {(() => {
                                // Calculate cooldown status specific to this challenge
                                const cooldownKey = challenge.id || globalIndex;
                                const currentCooldown = aiCooldowns[cooldownKey] ?? 0;
                                const isAiReady = currentCooldown === 0;

                                return (
                                  <Button
                                    variant="outlined" 
                                    size="small" 
                                    startIcon={isAiReady ? <SmartToyIcon /> : <CircularProgress size={14} color="inherit" />}
                                    disabled={!isAiReady}
                                    onClick={() => {
                                      setActiveChallengeForCatalog(challenge.id);
                                      dispatch(openSolutionChat({ challengeId: challenge.id }));
                                    }}
                                    sx={{ 
                                      textTransform: "none", 
                                      fontWeight: 600, 
                                      borderRadius: 1.5, 
                                      fontSize: "0.8rem", 
                                      borderColor: isAiReady ? default_theme.palette.other.chatGreen : 'grey.400', 
                                      color: isAiReady ? default_theme.palette.other.chatGreen : 'grey.500',
                                      transition: 'all 0.3s ease'
                                    }}
                                  >
                                    {isAiReady ? "AI Solutions Suggestion" : `AI Ready in ${currentCooldown}s`}
                                  </Button>
                                );
                              })()}
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleOpenCatalog(challenge.id)}
                                sx={{ 
                                  textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: "0.8rem",
                                  backgroundColor: default_theme.palette.other.chatGreen, color: "#fff",
                                  "&:hover": { backgroundColor: default_theme.palette.other.chatGreenHover }
                                }}
                              >
                                Browse Solutions
                              </Button>
                              
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => toggleLW2Selection(challenge.id, globalIndex)}
                                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, fontSize: "0.8rem", py: 0.3, px: 1 }}
                              >
                                Remove from LW2
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
              </Box>

              <QuestionCardDownload />

              {/* =========================
                  SOLUTION PROPOSAL TEMPLATE
              ========================= */}
              <Box className={styles.phaseDataPanel} sx={{ mt: 4 }}>
                <Box className={styles.phaseSectionLabel}>
                  <Box className={styles.phaseSectionDot} />
                  <Typography variant="h6" className={styles.phaseSectionTitle}>
                    Solution Proposal Template - LW2
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "#667085", mb: 3 }}>
                  Review the Question Cards completed by your groups and synthesize the ideas into initial solution proposals.
                  You may select up to 5 of the most promising practical solutions.
                  ({solutionProposals.length} / 5 Created)
                </Typography>

{/* Display Existing Proposals */}
                {solutionProposals.length > 0 && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {solutionProposals.map((proposal) => {
                      const relatedChallenge = lw2Challenges.find((c) => c.id === proposal.challenge_id);
                      return (
                        <Grid size={{ xs: 12 }} key={proposal.id}>
                          <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E4E7EC" }}>
                            <Typography sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
                              Addressing: {relatedChallenge ? relatedChallenge.title : "Unknown Challenge"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#475467", mb: 2, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                              {proposal.description}
                            </Typography>
                            
                            {/* BUTTONS MOVED TO LEFT */}
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={() => { setProposalFormData(proposal); setProposalModalOpen(true); }}
                                sx={{ textTransform: "none", fontWeight: 600, color: "#23576B", borderColor: "#23576B" }}
                              >
                                View / Edit Details
                              </Button>
                              <DangerButton onClick={() => confirmDeleteProposal(proposal.id)} sx={{ minWidth: "80px", p: "4px 12px", fontSize: "0.85rem" }}>
                                Delete
                              </DangerButton>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}

                {/* DELETE CONFIRMATION DIALOG */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                  <DialogTitle sx={{ fontWeight: "bold", color: "#d32f2f" }}>Delete Solution Proposal?</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Are you sure you want to delete this solution proposal? This action cannot be undone.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "#667085", fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={executeDeleteProposal} variant="contained" color="error" sx={{ fontWeight: 600 }}>Delete</Button>
                  </DialogActions>
                </Dialog>

                {/* Add Button (Disabled if 5 or more) */}
                <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                  <AddButton 
                    onClick={() => { setProposalFormData(null); setProposalModalOpen(true); }}
                    disabled={solutionProposals.length >= 5}
                  >
                    + Add Solution Proposal
                  </AddButton>
                </Box>
                
                {solutionProposals.length >= 5 && (
                  <Typography variant="caption" sx={{ color: "error.main", mt: 1, display: "block" }}>
                    Maximum of 5 solution proposals reached.
                  </Typography>
                )}

                <SolutionProposalDialog
                  open={proposalModalOpen}
                  onClose={() => setProposalModalOpen(false)}
                  onSave={handleSaveProposal}
                  initialData={proposalFormData}
                  challenges={lw2Challenges}
                  rdds={rdds}
                  stakeholders={stakeholders}
                />
              </Box>

          {/* =========================
             SUBSECTION 4: LW3 FINE-TUNING
          ========================= */}
          <Divider sx={{ mt: 4, mb: 4 }} />
          
          <Box sx={{ mt: 8 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#7898A4", mb: 3 }}
            >
              LOCAL WORKSHOP 3 — FINE-TUNING SOLUTIONS
            </Typography>

            <Typography variant="body1" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
              Turn promising ideas into actionable solutions
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
              The third Local Workshop takes the most promising solution proposals from Workshop 2 and develops them into detailed action plans.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
              Together with stakeholders, examine how each solution can work in practice, who needs to be involved, what resources are available, how the solution contributes to the cross-cutting priorities, and how it can be implemented and monitored.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 3 }}>
              The workshop should result in three to five detailed solution plans, depending on the number of promising proposals.
            </Typography>
          </Box>

          {/* ── INFO PANEL (learn): Develop each solution through five lenses ── */}
          <Box className={styles.phaseInfoPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Typography variant="h6" className={styles.phaseSectionTitle}>
                Develop each solution through five lenses
              </Typography>
            </Box>

            {[
              {
                step: "1",
                title: "Planning",
                bullets: [
                  "Objectives",
                  "Specific activities",
                  "Targets",
                  "Main and other beneficiaries",
                  "Stakeholders involved and their roles",
                ],
              },
              {
                step: "2",
                title: "Resources & Capitals",
                text: "Consider the resources and forms of capital needed:",
                bullets: [
                  "Cultural",
                  "Natural",
                  "Built",
                  "Social",
                  "Human",
                  "Data",
                  "Financial",
                ],
              },
              {
                step: "3",
                title: "Cross-cutting priorities & approach",
                text: "Consider:",
                bullets: [
                  "Climate change mitigation and adaptation",
                  "Biodiversity",
                  "Social justice and inclusion",
                  "Gender considerations",
                  "Groups at risk of exclusion",
                ],
              },
              {
                step: "4",
                title: "Forms of innovation",
                text: "Consider whether the solution involves:",
                bullets: [
                  "Digital and technological innovation",
                  "Technical innovation",
                  "Social, organisational and governance innovation",
                  "Financial and business model innovation",
                ],
              },
              {
                step: "5",
                title: "Implementation",
                bullets: [
                  "Activities and timeline",
                  "Budget and cost structure",
                  "Funding/cost sources",
                  "Sustainability",
                  "Long-term economic, environmental, social and cultural impacts",
                  "Potential synergies with other initiatives and projects",
                ],
              },
            ].map((item, index, arr) => (
              <Box key={item.step} sx={{ display: "flex", gap: 2, mb: index < arr.length - 1 ? 0 : 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 48 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #35B3AB, #23576B)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(53, 179, 171, 0.3)",
                    }}
                  >
                    {item.step}
                  </Box>
                  {index < arr.length - 1 && (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        minHeight: 30,
                        background: "linear-gradient(to bottom, #35B3AB, rgba(53, 179, 171, 0.2))",
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    p: 2.5,
                    mb: index < arr.length - 1 ? 3 : 0,
                    border: "1px solid rgba(35, 87, 107, 0.1)",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, rgba(35, 87, 107, 0.03), rgba(53, 179, 171, 0.02))",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
                    {item.title}
                  </Typography>
                  {item.text && (
                    <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 1 }}>
                      {item.text}
                    </Typography>
                  )}
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {item.bullets.map((b) => (
                      <li key={b}>
                        <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7 }}>{b}</Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              </Box>
            ))}

            <Typography variant="body1" sx={{ color: "#7898A4", mt: 2 }}>
              These elements correspond to the structure of the RURACTIVE Canva used to develop and refine solutions.
            </Typography>
          </Box>

          {/* ── INFO PANEL (learn): Keep the solution connected to the territory ── */}
          <Box className={styles.phaseInfoPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Typography variant="h6" className={styles.phaseSectionTitle}>
                Keep the solution connected to the territory
              </Typography>
            </Box>

            <Typography variant="body1" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
              A strong solution should not be developed in isolation.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
              Throughout the workshop, ask:
            </Typography>

            {[
              "Does it respond to a clearly identified territorial challenge?",
              "Does it contribute to the shared vision?",
              "Does it use or strengthen local resources and capabilities?",
              "Who needs to be involved for it to work?",
              "Does it address inclusion and cross-cutting priorities?",
              "Can it realistically be implemented and sustained?",
              "Can it build on existing initiatives or create useful synergies?",
            ].map((item) => (
              <Box className={styles.checkItem} key={item}>
                <CheckCircleOutlineIcon className={styles.checkIcon} />
                <Typography className={styles.checkText}>{item}</Typography>
              </Box>
            ))}

            <Typography variant="body1" sx={{ color: "#7898A4", mt: 2 }}>
              The purpose of the final workshop is not simply to complete a template. It is to turn a promising idea into a shared, realistic and actionable solution.
            </Typography>
          </Box>

          <Box sx={{ pl: 2, mt: 4 }}>
              {/* 1. The new Download Component */}
              <RuractiveCanvaDownload />

              {/* 2. Create Solution */}
              <Box className={styles.phaseDataPanel} sx={{ mb: 4 }}>
                <Box className={styles.phaseSectionLabel}>
                  <Box className={styles.phaseSectionDot} />
                  <Typography variant="h6" className={styles.phaseSectionTitle}>
                    Create a Solution
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: "#7898A4", mb: 3 }}>
                  Fill in the details below and save your work as a draft. You can publish it later.
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <AddButton onClick={() => setOpenSolutionDialog(true)}>
                    Create solution
                  </AddButton>

                  <SolutionFormDialog
                    open={openSolutionDialog}
                    onClose={() => setOpenSolutionDialog(false)}
                    onSave={saveSolution}
                    form={solutionForm}
                    onChange={handleSolutionChange}
                  />
                </Box>
              </Box>

              {/* 3. Saved Drafts */}
              <Box className={styles.phaseDataPanel} sx={{ mb: 4 }}>
                <Box className={styles.phaseSectionLabel}>
                  <Box className={styles.phaseSectionDot} />
                  <Typography variant="h6" className={styles.phaseSectionTitle}>
                    Saved Drafts
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: "#7898A4", mb: 3 }}>
                  Review the drafts you have already saved. Use View Details to inspect a draft, or edit and delete it from the dialog.
                </Typography>

                <DraftSolutions />
              </Box>
            </Box>

        </Box> {/* This closes the entire Phase 3.2 Box */}

        <FigmaBlueDivider sx={{ mt: 6 }} />

        {/* Phase 3.3 */}
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#7898A4", mb: 3 }}
          >
            PHASE 3.3 — PRESENTING THE LOCAL ACTION PLAN
          </Typography>

          <Typography variant="body1" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
            Bring your co-development process together
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
            Your workshops have produced a shared territorial vision, identified challenges, explored existing solutions and developed detailed solution plans.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 2 }}>
            Now bring these results together into your Local Action Plan (LAP).
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", lineHeight: 1.7, mb: 3 }}>
            The LAP should provide a clear reference for moving from co-development to implementation. It should bring together the main results of the RIE process and the implementation plans for the selected solutions.
          </Typography>
        </Box>

        {/* ── INFO PANEL (learn): What goes into the Local Action Plan? ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Typography variant="h6" className={styles.phaseSectionTitle}>
              What goes into the Local Action Plan?
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            The LAP should bring together:
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
            {[
              {
                title: "Your RIE",
                items: [
                  "Territory",
                  "Selected RDDs",
                  "RIE community",
                  "Stakeholder engagement",
                ],
              },
              {
                title: "Your co-development process",
                items: [
                  "Local Workshop activities",
                  "Participants",
                  "Vision",
                  "Challenges",
                  "Key workshop outcomes",
                ],
              },
              {
                title: "Your solutions",
                items: [
                  "Selected solutions",
                  "Challenges addressed",
                  "Stakeholders involved",
                  "Cross-cutting priorities",
                  "Innovation approach",
                ],
              },
              {
                title: "Your implementation plans",
                items: [
                  "Objectives",
                  "Activities",
                  "Targets",
                  "Resources",
                  "Budget",
                  "Timeline",
                  "Sustainability",
                  "Expected impacts",
                ],
              },
            ].map((section) => (
              <Box key={section.title} sx={{ p: 2, borderRadius: "12px", border: "1px solid rgba(35, 87, 107, 0.1)", background: "rgba(53, 179, 171, 0.04)" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
                  {section.title}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {section.items.map((item) => (
                    <li key={item}>
                      <Typography variant="body2" sx={{ color: "#7898A4", lineHeight: 1.7 }}>{item}</Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4" }}>
            The information should be drawn automatically from the work already completed in Steps 0–3, with the possibility for the user to review and refine it before publication.
          </Typography>
        </Box>

        <LocalActionPlanSection projectId={projectId} />

      </Box>

      {/* FILE DELETE DIALOG */}
      <Dialog open={Boolean(fileToDelete)} onClose={() => setFileToDelete(null)}>
        <DialogTitle sx={{ fontWeight: "bold", color: "#d32f2f" }}>Delete Document?</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to remove this document? You will need to upload it again if you change your mind.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFileToDelete(null)} sx={{ color: "#667085", fontWeight: 600 }}>Cancel</Button>
          <Button 
            onClick={() => { handleDeleteFile(fileToDelete); setFileToDelete(null); }} 
            variant="contained" color="error" sx={{ fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* CHALLENGE DELETE DIALOG */}
      <Dialog open={Boolean(challengeToDelete)} onClose={() => setChallengeToDelete(null)}>
        <DialogTitle sx={{ fontWeight: "bold", color: "#d32f2f" }}>Delete Challenge?</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this territorial challenge? If it is selected for LW2, it will be removed from there as well.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setChallengeToDelete(null)} sx={{ color: "#667085", fontWeight: 600 }}>Cancel</Button>
          <Button 
            onClick={() => { handleDeleteChallenge(challengeToDelete); setChallengeToDelete(null); }} 
            variant="contained" color="error" sx={{ fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* CATALOG DIALOG */}
      <SolutionsCatalogDialog 
        open={catalogDialogOpen}
        onClose={() => { setCatalogDialogOpen(false); setActiveChallengeForCatalog(null); }}
        onAttach={handleAttachSolution}
        onDetach={(solutionId) => handleRemoveAttachment(activeChallengeForCatalog, solutionId)}
        attachedCount={
          localChallenges.find(c => c.id === activeChallengeForCatalog)?.attached_solutions?.length || 0
        }
        existingAttachments={
          localChallenges.find(c => c.id === activeChallengeForCatalog)?.attached_solutions || []
        }
      />

      {/* ---  SNACKBAR  --- */}
      <Snackbar 
        open={maxLimitWarning} 
        autoHideDuration={4000} 
        onClose={() => setMaxLimitWarning(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert 
          onClose={() => setMaxLimitWarning(false)} 
          severity="warning" 
          sx={{ width: "100%", fontWeight: 600 }}
        >
          You can only add a maximum of 8 challenges to the LW2 Workshop.
        </Alert>
      </Snackbar>
      <SolutionChatPopup projectId={projectId} onAttachSolution={handleAttachSolution} />
    </StepLayout>
  );
};

export default StepThreePage;